/**
 * Calculate total experience from multiple job periods
 * @param {Array<Array<Object>>} periods - Array of job period arrays
 * @returns {Object} - Object containing years, months, totalMonths, and displayText
 *
 * Example:
 * const periods = [
 *   [{startDate: '2020-01', endDate: '2021-06'}],
 *   [{startDate: '2021-04', endDate: '2023-12'}]
 * ];
 * const result = getTotalExperience(periods);
 * // Returns: { years: 3, months: 11, totalMonths: 47, displayText: "3 yr 11 mos" }
 */
function getTotalExperience(periods) {
  if (!periods || !Array.isArray(periods)) {
    return { years: 0, months: 0, totalMonths: 0, displayText: '0 mos' }
  }

  let totalMonths = 0

  // Iterate through each period array
  periods.forEach((period) => {
    if (period && period.startDate) {
      const months = calculateMonthsBetweenDates(
        period.startDate,
        period.endDate
      )
      totalMonths += months
    }
  })

  // Convert total months to years and remaining months
  const years = Math.floor(totalMonths / 12)
  const remainingMonths = totalMonths % 12

  // Generate display text
  const displayText = formatExperienceDisplay(years, remainingMonths)

  return {
    years,
    months: remainingMonths,
    totalMonths,
    displayText,
  }
}

/**
 * Calculate the number of months between two dates in 'YYYY-MM' format
 * @param {string} startDate - Start date in 'YYYY-MM' format
 * @param {string|null} endDate - End date in 'YYYY-MM' format, or null for present
 * @returns {number} - Number of months between the dates (inclusive)
 */
function calculateMonthsBetweenDates(startDate, endDate) {
  try {
    const [startYear, startMonth] = startDate.split('-').map(Number)

    let endYear, endMonth

    // If endDate is null, use current date (present)
    if (endDate === null || endDate === undefined) {
      const currentDate = new Date()
      endYear = currentDate.getFullYear()
      endMonth = currentDate.getMonth() + 1 // getMonth() returns 0-11, so add 1
    } else {
      ;[endYear, endMonth] = endDate.split('-').map(Number)
    }

    // Calculate the difference in months
    const yearDiff = endYear - startYear
    const monthDiff = endMonth - startMonth

    // Total months (inclusive of both start and end months)
    return yearDiff * 12 + monthDiff + 1
  } catch (error) {
    return 0
  }
}

/**
 * Format experience into display text
 * @param {number} years - Number of years
 * @param {number} months - Number of remaining months
 * @returns {string} - Formatted display text
 */
function formatExperienceDisplay(years, months) {
  if (years === 0) {
    // Only months: "5 mos"
    return `${months} mos`
  } else if (years === 1) {
    if (months === 0) {
      // Exactly 1 year: "1 yr"
      return '1 yr'
    } else {
      // 1 year + months: "1 yr 3 mos"
      return `1 yr ${months} mos`
    }
  } else {
    if (months === 0) {
      // Multiple years, no months: "3 yrs"
      return `${years} yrs`
    } else {
      // Multiple years + months: "3 yr 10 mos"
      return `${years} yr ${months} mos`
    }
  }
}

// Export the function for use in other modules
export default getTotalExperience

// Also export as named export for flexibility
export { getTotalExperience }
